import { type GQLError, type HttpError } from '@egw/clients'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  PendingJointAcatTransfersOutput,
  type BankingInstitution,
  type FileUploadError,
  type FileUploadSuccess,
  type GetBankInstitutionNameError,
  type InvestmentTransferValidationInput,
  type InvestmentTransferValidationOutput,
  type Wire,
} from '../../models'
import {
  getAchInstitutionName,
  getInvestmentTransferValidation,
  getPendingJointAcatTransfers,
  getWire,
  getWireInstitutionName,
  uploadAcatDocument,
  uploadManualReviewDocument,
} from '../../services'

/**
 * Get Investment Transfer Validation
 * @param investmentTransferValidationInput - InvestmentTransferValidationInput
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTransferValidationOutput>
 * @example const investmentTransferValidation = await getInvestmentTransferValidation(\{ alias: number, isTo: boolean \});
 */
export const useInvestmentTransferValidation = (
  investmentTransferValidationInput: InvestmentTransferValidationInput,
  options?: Omit<UseQueryOptions<InvestmentTransferValidationOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentTransferValidationOutput, GQLError> =>
  useQuery<InvestmentTransferValidationOutput, GQLError>({
    queryKey: ['getInvestmentTransferValidation', investmentTransferValidationInput, investmentTransferValidationInput],
    queryFn: () => getInvestmentTransferValidation(investmentTransferValidationInput),
    ...options,
  })

/**
 * Get ACH institution name
 * @param routingNumber - routing number of the bank
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns bank name or error
 */
export const useAchInstitutionName = (
  routingNumber: string,
  options?: Omit<
    UseQueryOptions<BankingInstitution | GetBankInstitutionNameError, GQLError>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<BankingInstitution | GetBankInstitutionNameError, GQLError> =>
  useQuery<BankingInstitution | GetBankInstitutionNameError, GQLError>({
    queryKey: ['getAchInstitutionName', routingNumber],
    queryFn: () => getAchInstitutionName(routingNumber),
    ...options,
  })

/**
 * Get Wire transfer institution name
 * @param routingNumber - routing number of the bank
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns bank name or error
 */
export const useWireInstitutionName = (
  routingNumber: string,
  options?: Omit<
    UseQueryOptions<BankingInstitution | GetBankInstitutionNameError, GQLError>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<BankingInstitution | GetBankInstitutionNameError, GQLError> =>
  useQuery<BankingInstitution | GetBankInstitutionNameError, GQLError>({
    queryKey: ['getWireInstitutionName', routingNumber],
    queryFn: () => getWireInstitutionName(routingNumber),
    ...options,
  })

/**
 * Get Wire transfer details
 * @param id - wire transfer id
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns wire transfer details
 */
export const useWire = (
  id: string,
  options?: Omit<UseQueryOptions<Wire, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<Wire, GQLError> =>
  useQuery<Wire, GQLError>({
    queryKey: ['getWire', id],
    queryFn: () => getWire(id),
    ...options,
  })

/**
 * Upload manual review document
 */
export const useUploadManualReviewDocument = (
  applicationId: string,
): UseMutationResult<FileUploadSuccess | FileUploadError, HttpError, File> =>
  useMutation<FileUploadSuccess | FileUploadError, HttpError, File>({
    mutationFn: (file: File) => uploadManualReviewDocument(applicationId, file),
  })

/**
 * Upload ACAT document
 */
export const useUploadAcatDocument = (): UseMutationResult<FileUploadSuccess | FileUploadError, HttpError, File> =>
  useMutation<FileUploadSuccess | FileUploadError, HttpError, File>({
    mutationFn: (file: File) => uploadAcatDocument(file),
  })

/**
 * Get pending joint ACAT transfers
 */
export const useGetPendingJointAcatTransfers = (
  options?: Omit<UseQueryOptions<PendingJointAcatTransfersOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<PendingJointAcatTransfersOutput, GQLError> =>
  useQuery<PendingJointAcatTransfersOutput, GQLError>({
    queryKey: ['pendingJointAcatTransfers'],
    queryFn: () => getPendingJointAcatTransfers(),
    ...options,
  })
